import { InMemoryCache } from '@apollo/client';
import { CachePersistor } from 'apollo3-cache-persist';
import localForage from 'localforage';
import typePolicies from './typePolicies';
import apolloPossibleTypes from '../../graphql/generated/apolloPossibleTypes';
import Logger from '../../utils/Logger';

const CACHE_MAX_SIZE_MB = 100;
const CACHE_SCHEMA_VERSION_KEY = 'apolloCacheVersion';
const CACHE_KEY = 'apolloCache';

const localForageInstance = localForage.createInstance({
  name: 'slab-cache-persister-a3',
});

const cache = new InMemoryCache({
  possibleTypes: apolloPossibleTypes.possibleTypes,
  typePolicies,
});

class LocalForageWrapper {
  constructor(private storage: LocalForage) {}

  getItem(key: string): string | Promise<string | null> | null {
    return this.storage.getItem(key);
  }

  removeItem(key: string): void | Promise<void> {
    return this.storage.removeItem(key);
  }

  setItem(key: string, value: string): void | Promise<void> {
    return new Promise((resolve) => {
      this.storage
        .setItem(key, value)
        .then(() => resolve())
        .catch((err) => {
          Logger.warn(err);
          cachePersistor.remove();
        });
    });
  }
}

const cachePersistor = new CachePersistor({
  cache,
  debug: process.env.NODE_ENV !== 'production',
  key: CACHE_KEY,
  maxSize: CACHE_MAX_SIZE_MB * 1024 * 1024,
  storage: new LocalForageWrapper(localForageInstance),
});

export function purgeApolloCache() {
  cachePersistor.purge();
}

export async function tryRestore() {
  const currentVersion = process.env.GRAPHQL_SCHEMA_HASH as string;

  try {
    if (currentVersion === localStorage.getItem(CACHE_SCHEMA_VERSION_KEY)) {
      await cachePersistor.restore();
    } else {
      await cachePersistor.purge();
      localStorage.setItem(CACHE_SCHEMA_VERSION_KEY, currentVersion);
    }
  } catch (error) {
    Logger.error(error);
  }
}

export default cache;
